/**
 * Text area with non-deletable placeholder tokens (e.g. {{landlord_name}}).
 * Placeholders are rendered as inline chips; only the text between them is editable.
 */

import React, { useRef, useCallback, useEffect } from 'react';
import styled from 'styled-components';

const PLACEHOLDER_REGEX = /(\{\{[^}]+\}\})/g;

function splitSegments(value: string): string[] {
  if (!value.trim()) return [''];
  return value.split(PLACEHOLDER_REGEX);
}

function isPlaceholder(segment: string): boolean {
  return /^\{\{[^}]+\}\}$/.test(segment);
}

const EditorWrap = styled.div<{ $minHeight?: number }>`
  width: 100%;
  min-height: ${(p) => p.$minHeight ?? 120}px;
  padding: 12px 16px;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: 12px;
  background: ${(p) => p.theme.background};
  color: ${(p) => p.theme.foreground};
  font-size: 14px;
  font-family: inherit;
  line-height: 1.5;
  transition: border-color 0.2s ease;
  white-space: pre-wrap;
  word-break: break-word;

  &:focus-within {
    outline: none;
    border-color: ${(p) => p.theme.foreground};
    box-shadow: 0 0 0 2px ${(p) => p.theme.muted}40;
  }

  &[data-placeholder]:empty::before {
    content: attr(data-placeholder);
    color: ${(p) => p.theme.mutedForeground};
  }

  .placeholder-chip-inner {
    display: inline-flex;
    align-items: center;
    padding: 2px 5px;
    margin: 0 1px;
    border-radius: 4px;
    background: ${(p) => p.theme.muted}50;
    color: ${(p) => p.theme.foreground};
    border: 1px solid ${(p) => p.theme.border};
    font-size: 10px;
    font-weight: 500;
    font-family: ui-monospace, 'SF Mono', Monaco, monospace;
    user-select: none;
    white-space: nowrap;
  }
`;

export interface PlaceholderEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: number;
  id?: string;
  'aria-label'?: string;
}

export function PlaceholderEditor({
  value,
  onChange,
  placeholder = '',
  minHeight = 120,
  id,
  'aria-label': ariaLabel,
}: PlaceholderEditorProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const valueFromInputRef = useRef<string | null>(null);

  const readValueFromDom = useCallback((): string => {
    const el = wrapRef.current;
    if (!el) return value;
    const parts: string[] = [];
    const walk = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        parts.push(node.textContent || '');
        return;
      }
      if (node.nodeType === Node.ELEMENT_NODE) {
        const elNode = node as HTMLElement;
        if (elNode.classList.contains('placeholder-chip-inner')) {
          parts.push(elNode.textContent || '');
          return;
        }
        node.childNodes.forEach(walk);
      }
    };
    el.childNodes.forEach(walk);
    return parts.join('');
  }, [value]);

  const handleInput = useCallback(() => {
    const newVal = readValueFromDom();
    if (newVal !== value) {
      valueFromInputRef.current = newVal;
      onChange(newVal);
    }
  }, [value, onChange, readValueFromDom]);

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      const text = e.clipboardData.getData('text/plain');
      document.execCommand('insertText', false, text);
      setTimeout(handleInput, 0);
    },
    [handleInput]
  );

  // Sync value from props into the DOM only when the change is "external" (e.g. reset), not from our own onChange
  const syncValueToDom = useCallback(
    (val: string) => {
      const el = wrapRef.current;
      if (!el) return;
      const segments = splitSegments(val);
      const fragment = document.createDocumentFragment();
      segments.forEach((seg) => {
        if (isPlaceholder(seg)) {
          const span = document.createElement('span');
          span.contentEditable = 'false';
          span.className = 'placeholder-chip-inner';
          span.textContent = seg;
          fragment.appendChild(span);
        } else {
          fragment.appendChild(document.createTextNode(seg));
        }
      });
      el.innerHTML = '';
      el.appendChild(fragment);
    },
    []
  );

  useEffect(() => {
    if (value === valueFromInputRef.current) {
      valueFromInputRef.current = null;
      return;
    }
    valueFromInputRef.current = null;
    syncValueToDom(value);
  }, [value, syncValueToDom]);

  const isEmpty = !value.trim();

  if (isEmpty) {
    return (
      <EditorWrap
        ref={wrapRef}
        $minHeight={minHeight}
        id={id}
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={handleInput}
        onPaste={handlePaste}
        aria-label={ariaLabel}
        role="textbox"
      />
    );
  }

  return (
    <EditorWrap
      ref={wrapRef}
      $minHeight={minHeight}
      id={id}
      contentEditable
      suppressContentEditableWarning
      onInput={handleInput}
      onPaste={handlePaste}
      aria-label={ariaLabel}
      role="textbox"
    />
  );
}
